import type { EnvironmentProviders, Provider } from '@angular/core';
import { inject, LOCALE_ID, provideAppInitializer, provideEnvironmentInitializer } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeNbNo from '@angular/common/locales/nb';
import localeNnNo from '@angular/common/locales/nn';
import { DEFAULT_LANGUAGE } from '@core/constants/language';
import { Language, SUPPORTED_LANGUAGES } from '@core/enums/language';
import { LogMissingTranslationHandler } from '@core/providers/log-missing-translation-handler';
import { ModuleTranslateLoader } from '@core/providers/module-translate-loader';
import { CriticalError } from '@core/services/critical-error';
import { Storage } from '@core/services/storage';
import {
  provideMissingTranslationHandler,
  provideTranslateLoader,
  provideTranslateService,
  TranslateService
} from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { getLanguageFromLocationParam } from '@utils/location';
import { getNavigatorLanguage } from '@utils/navigator';

export function provideTranslation(): Provider | EnvironmentProviders[] {
  // Manually create storage service since DI is not available yet
  const storageService = new Storage(localStorage);

  // Get language from query param, local storage, navigator, or use default language
  const initialLanguage =
    getLanguageFromLocationParam() ?? storageService.getLanguage() ?? getNavigatorLanguage() ?? DEFAULT_LANGUAGE;

  // Save the language to local storage
  storageService.setLanguage(initialLanguage);

  // Set the language tag in the Sentry scope
  Sentry.getGlobalScope().setTag('language', initialLanguage);

  return [
    provideTranslateService({
      loader: provideTranslateLoader(() => new ModuleTranslateLoader(inject(CriticalError))),
      missingTranslationHandler: provideMissingTranslationHandler(
        () => new LogMissingTranslationHandler(inject(CriticalError))
      ),
      fallbackLang: DEFAULT_LANGUAGE
    }),
    {
      provide: LOCALE_ID,
      useValue: initialLanguage
    },
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      translate.addLangs(SUPPORTED_LANGUAGES);
      translate.setFallbackLang(DEFAULT_LANGUAGE);
      return translate.use(initialLanguage);
    }),
    provideEnvironmentInitializer(() => {
      registerLocaleData(localeEn, Language.English);
      registerLocaleData(localeNbNo, Language.Bokmal);
      registerLocaleData(localeNnNo, Language.Nynorsk);
    })
  ];
}
